import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import ruTranslations from '../locales/ru.json';
import enTranslations from '../locales/en.json';

type Language = 'ru' | 'en';
type Translations = Record<string, string>;

interface UseLocalizationReturn {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: string, params?: Record<string, string | number>) => string;
    tHtml: (key: string, params?: Record<string, string | number>) => ReactNode;
}

const translations: Record<Language, Translations> = {
    ru: ruTranslations,
    en: enTranslations
};

// Функция для замены параметров в строке
const replaceParams = (text: string, params?: Record<string, string | number>): string => {
    if (!params) return text;

    return Object.keys(params).reduce((result, key) => {
        const value = params[key];
        return result.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value));
    }, text);
};

export const useLocalization = (): UseLocalizationReturn => {
    const [language, setLanguageState] = useState<Language>('ru');

    const getLanguageFromCookie = useCallback((): Language => {
        if (typeof document === 'undefined') return 'ru';

        const cookies = document.cookie.split(';');
        const langCookie = cookies.find(cookie => cookie.trim().startsWith('ln='));

        if (langCookie) {
            const langValue = langCookie.split('=')[1]?.trim();
            if (langValue === 'ru' || langValue === 'en') {
                return langValue;
            }
        }

        return 'ru';
    }, []);

    const setLanguage = useCallback((lang: Language) => {
        setLanguageState(lang);
        document.cookie = `ln=${lang}; max-age=${86400 * 365}; path=/`;
    }, []);

    // Обычный перевод (возвращает строку)
    const t = useCallback((key: string, params?: Record<string, string | number>): string => {
        const translation = translations[language]?.[key];

        if (!translation) {
            console.warn(`Translation missing for key: "${key}" in language: ${language}`);
            return key;
        }

        return replaceParams(translation, params);
    }, [language]);

    // HTML перевод (возвращает ReactNode с поддержкой HTML)
    const tHtml = useCallback((key: string, params?: Record<string, string | number>): ReactNode => {
        const translation = translations[language]?.[key];

        if (!translation) {
            console.warn(`Translation missing for key: "${key}" in language: ${language}`);
            return key;
        }

        const textWithParams = replaceParams(translation, params);

        // Создаем элемент span с dangerouslySetInnerHTML
        return React.createElement('span', {
            dangerouslySetInnerHTML: { __html: textWithParams }
        });
    }, [language]);

    useEffect(() => {
        const currentLang = getLanguageFromCookie();
        setLanguageState(currentLang);
    }, [getLanguageFromCookie]);

    return {
        language,
        setLanguage,
        t,
        tHtml
    };
};